import Link from 'next/link'
import React, { useEffect, useRef, useState } from 'react'

import type { View } from '../twinContent'
import { ArrowRight } from '../../icons'
import { ENQUIRY_PATH } from '../../paths'
import { EXIT_LABEL, RAIL_LABELS } from '../twinContent'
import { TwinStepper } from './TwinStepper'

type Props = {
  step: number
  stepTotal: number
  levelName: string
  primaryLabel: string
  canGoBack: boolean
  canGoForward: boolean
  onBack: () => void
  onForward: () => void
  onJump: (view: View) => void
  onReset: () => void
}

export function TwinControlBar({
  step,
  stepTotal,
  levelName,
  primaryLabel,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
  onJump,
  onReset,
}: Props) {
  const [collapsed, setCollapsed] = useState(false)
  const tab = useRef<HTMLButtonElement>(null)
  const mini = useRef<HTMLButtonElement>(null)

  // The button that was pressed slides away, so a keyboard user's focus follows to its counterpart.
  useEffect(() => {
    const from = collapsed ? tab.current : mini.current
    if (document.activeElement === from)
      (collapsed ? mini : tab).current?.focus({ preventScroll: true })
  }, [collapsed])

  const collapseLabel = collapsed ? RAIL_LABELS.collapse.show : RAIL_LABELS.collapse.hide
  const toggle = () => setCollapsed((c) => !c)

  return (
    <>
      <button
        id="rail-mini"
        ref={mini}
        type="button"
        className={collapsed ? 'shown' : undefined}
        inert={!collapsed}
        aria-expanded={!collapsed}
        aria-controls="controls"
        title={RAIL_LABELS.collapse.show}
        onClick={toggle}
      >
        <span className="sr-only">{RAIL_LABELS.collapse.show}: </span>
        <span className="mini-count">
          {String(step + 1).padStart(2, '0')} / {String(stepTotal).padStart(2, '0')}
        </span>
        <span className="sr-only">, </span>
        <span className="mini-name">{levelName}</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 15 6-6 6 6" />
        </svg>
      </button>

      <footer id="footer" className={collapsed ? 'collapsed' : undefined}>
        {/* Below `md` the drawer collapses from this caret, above it from #footer-toggle. It sits outside
          #controls so it stays reachable once that goes inert. */}
        <button
          id="drawer-handle"
          type="button"
          aria-expanded={!collapsed}
          aria-controls="controls"
          aria-label={collapseLabel}
          title={collapseLabel}
          onClick={toggle}
        >
          <svg
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m3 4.5 3 3 3-3" />
          </svg>
        </button>

        <div id="footer-row">
          <div id="controls" inert={collapsed}>
            <div id="controls-normal">
              <button
                className="ctrl"
                id="btn-back"
                type="button"
                disabled={!canGoBack}
                onClick={onBack}
              >
                <ArrowRight className="size-3 rotate-180" />
                <span className="back-label">{RAIL_LABELS.back}</span>
              </button>
              <span className="divider before-stepper" aria-hidden="true" />

              <TwinStepper step={step} total={stepTotal} levelName={levelName} onJump={onJump} />

              <span className="divider after-stepper" aria-hidden="true" />
              {canGoForward ? (
                <button className="ctrl primary" id="btn-forward" type="button" onClick={onForward}>
                  {primaryLabel}
                  <ArrowRight className="size-3" />
                </button>
              ) : (
                <Link className="ctrl primary" id="btn-forward" href={ENQUIRY_PATH}>
                  {EXIT_LABEL}
                  <ArrowRight className="size-3" />
                </Link>
              )}
              <span className="divider" aria-hidden="true" />
              <button
                className="icon-btn"
                id="btn-reset"
                type="button"
                aria-label={RAIL_LABELS.reset.aria}
                title={RAIL_LABELS.reset.title}
                onClick={onReset}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
              </button>
            </div>
          </div>

          <button
            id="footer-toggle"
            ref={tab}
            type="button"
            aria-expanded={!collapsed}
            aria-controls="controls"
            aria-label={collapseLabel}
            title={collapseLabel}
            onClick={toggle}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>
      </footer>
    </>
  )
}
